import lodashGet from "lodash/get";
import React, {
  ComponentPropsWithoutRef,
  isValidElement,
  cloneElement,
} from "react";
import { PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";

import { DeepKeys } from "shared/types/deepkeys";

export type PillarTableBooleanIconType = React.ReactElement<
  React.SVGProps<SVGSVGElement> & {
    className?: string;
    title?: string;
  }
>;
export type PillarTableColumnBooleanProps<T> = {
  trueValue?: string;
  falseValue?: string;
  trueText?: string;
  falseText?: string;
  trueIcon?: PillarTableBooleanIconType;
  falseIcon?: PillarTableBooleanIconType;
} & PillarTableBaseColumnCellValuePathProps<T> &
  ComponentPropsWithoutRef<"div">;

const PillarTableColumnBoolean = <T,>({
  cellValuePath = "" as DeepKeys<T>,
  trueText = "True",
  falseText = "False",
  trueIcon,
  falseIcon,
  className,
  ...props
}: PillarTableColumnBooleanProps<T>) => {
  const rowContext = usePillarTableRowContext();
  const cellValue = lodashGet(rowContext.rowValue, cellValuePath) as
    | string
    | boolean
    | object
    | undefined;

  const objectExists =
    cellValue &&
    typeof cellValue === "object" &&
    Object.keys(cellValue).length > 0;

  const renderIcon = (
    icon: React.ReactElement<
      React.SVGProps<SVGSVGElement> & {
        className?: string;
        title?: string;
      }
    >,
    altText: string,
  ) => {
    if (isValidElement(icon)) {
      const iconType = icon.type;
      if (typeof iconType === "string" && iconType === "svg") {
        return cloneElement(icon, {
          className,
          "aria-label": altText,
          title: altText,
        });
      }
    }
    return icon;
  };

  if (objectExists || cellValue === true || cellValue === trueText) {
    if (trueIcon) {
      return <div {...props}>{renderIcon(trueIcon, trueText)}</div>;
    }
    return (
      <div className={className} {...props}>
        {trueText}
      </div>
    );
  }
  if (falseIcon) {
    return <div {...props}>{renderIcon(falseIcon, falseText)}</div>;
  }
  return (
    <div className={className} {...props}>
      {falseText}
    </div>
  );
};
export default PillarTableColumnBoolean;
